import { CodeNode, SerializedCodeNode } from '@lexical/code'
import { EditorConfig } from 'lexical'
import styles from '~/components/documents/code.module.scss'
import { cm } from '~/utils/utils'
import { DocReviewManager } from './DocReviewManager'

export class SyntaxHighlightCodeNode extends CodeNode {
  private text: string = ''
  private reviewManager!: DocReviewManager
  private _hash = Math.random().toString(36).substring(2, 7)

  private createNewLine: boolean = false

  setCreateNewLine(value: boolean) {
    this.createNewLine = value
  }

  static getType() {
    return 'editor-shiki-code'
  }

  constructor(
    public fullDocumentMarkdown: string,
    lang?: string | null,
    key?: string
  ) {
    super(lang, key)
  }

  static clone(node: SyntaxHighlightCodeNode, fullDocumentMarkdown?: string) {
    const markdownToUse = fullDocumentMarkdown ?? node.fullDocumentMarkdown
    return new SyntaxHighlightCodeNode(markdownToUse, node.getLanguage(), node.getKey())
  }

  exportJSON(): SerializedCodeNode {
    return {
      ...super.exportJSON(),
      language: this.getLanguage(),
      type: this.getType(),
      version: 1
    }
  }

  static importJSON(serializedNode: SerializedCodeNode): SyntaxHighlightCodeNode {
    const node = new SyntaxHighlightCodeNode('', serializedNode.language)
    return node
  }

  createDOM(config: EditorConfig) {
    const firstChild = this.getFirstChild()
    if (firstChild?.getType() === this.getType()) {
      const element = document.createElement('pre')
      element.classList.add(
        'editor-pre',
        'bg-transparent',
        'border-0',
        'p-0',
        'ml-[-0.5rem]',
        'overflow-visible'
      )
      element.style.overflow = 'visible'
      return element
    }
    const element = super.createDOM(config)
    const copyButton = this.createCopyButton()
    element.classList.add(
      'editor-code',
      '!bg-transparent',
      'border-0',
      '!p-0',
      'before:!hidden',
      'after:!hidden',
      'relative',
      'overflow-visible',
      '!pl-2'
    )

    element.contentEditable = 'false'
    element.style.overflow = 'visible'
    element.appendChild(copyButton)

    this.createReviewManager()
    this.reviewManager.setDomElement(element)

    if (this.reviewManager != null) {
      element.addEventListener('mousemove', e => this.reviewManager.handleMouseMove(e))
      element.addEventListener('mouseleave', () => this.reviewManager.hideAllButtons())
    }

    return element
  }

  private createReviewManager() {
    this.text = this.getTextContent()
    this.reviewManager = new DocReviewManager(this._hash, this.text, this.createNewLine)
  }

  private createCopyButton() {
    const button = document.createElement('button')
    button.className = cm(
      'flex gap-1.5 items-center rounded bg-none p-1 text-xs absolute right-0 top-0 z-50',
      'bg-white select-none rounded hover:bg-[#ececec] p-2 text-[#1b213fcc] active:opacity-80',
      styles.copyButton
    )
    button.contentEditable = 'false'
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('stroke', 'currentColor')
    svg.setAttribute('fill', 'currentColor')
    svg.setAttribute('viewBox', '0 0 512 512')
    svg.setAttribute('height', '16')
    svg.setAttribute('width', '16')
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    rect.setAttribute('width', '336')
    rect.setAttribute('height', '336')
    rect.setAttribute('x', '128')
    rect.setAttribute('y', '128')
    rect.setAttribute('fill', 'none')
    rect.setAttribute('stroke-linejoin', 'round')
    rect.setAttribute('stroke-width', '32')
    rect.setAttribute('rx', '57')
    rect.setAttribute('ry', '57')
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('fill', 'none')
    path.setAttribute('stroke-linejoin', 'round')
    path.setAttribute('stroke-width', '32')
    path.setAttribute(
      'd',
      'm383.5 128 .5-24a56.16 56.16 0 0 0-56-56H112a64.19 64.19 0 0 0-64 64v216a56.16 56.16 0 0 0 56 56h24'
    )
    svg.append(rect, path)
    button.appendChild(svg)

    button.addEventListener('click', () => {
      if (!navigator.clipboard || !navigator.clipboard.writeText) return
      navigator.clipboard.writeText(this.text.trim())
    })

    return button
  }

  updateDOM(prevNode: CodeNode, dom: HTMLElement, config: EditorConfig): boolean {
    return super.updateDOM(prevNode, dom, config)
  }
}
